<template>
	<div class="amelipro-result-list">
		<div
			v-if="!noResultListInfos"
			class="d-flex justify-space-between align-end mb-6 amelipro-result-list__infos"
		>
			<p class="d-inline-block align-center mb-0 total-counter font-weight-semibold">
				<span class="d-sr-only">Nombre de ligne dans le tableau ci-après :</span>
				{{ items.length }} {{ counterLabel }}
			</p>

			<div class="d-inline-flex align-center amelipro-result-list__select__wrapper">
				<AmeliproSelect
					v-if="sortSelectItems.length > 0 && sortSelect"
					:id="`${id}-sort-select`"
					ref="ameliproSortSelect"
					class="ml-2 amelipro-result-list__sort-select"
					global-max-width="170px"
					hide-error-message
					:items="sortSelectItems"
					label="Trier le tableau"
					placeholder="Trier par"
					style="padding-top: 0 !important;"
					@change="emitSortSelectChange()"
				/>

				<AmeliproSelect
					v-if="breakpointHelper.isDesktop() && paginationSelect"
					:id="`${id}-pagination-select`"
					ref="ameliproPaginationSelect"
					class="ml-2 amelipro-result-list__pagination-select"
					global-max-width="170px"
					hide-error-message
					:items="paginationSelectItems"
					label="Nombre de lignes par page"
					placeholder="Nb lignes/pages"
					style="padding-top: 0 !important;"
					@change="emitPaginationSelectChange()"
				/>
			</div>
		</div>
		<div>
			<ul
				:aria-label="title"
				class="list-style-none amelipro-result-list__list"
			>
				<li
					v-for="(item, index) in currentItems"
					:key="index"
					class="amelipro-result-list__item"
				>
					<slot
						:name="`result-${item.id}`"
						v-bind="item"
					>
						<slot
							name="result"
							v-bind="item"
						/>
					</slot>
				</li>
			</ul>
		</div>

		<AmeliproPagination
			v-if="paginationTable !== undefined && paginationTable.length > 0"
			:active-page-default="1"
			class="mt-2"
			:items="paginationTable"
			@click="emitClickEvent"
		/>
	</div>
</template>

<script setup lang="ts">
	import { AmeliproPaginationSelect, AmeliproSortSelect } from './types';
	import { PropType, computed, onMounted, onUpdated, ref, watch } from 'vue';
	import AmeliproPagination from '../AmeliproPagination';
	import AmeliproSelect from '../AmeliproSelect';
	import BreakpointHelper from '../../helpers/BreakpointHelper';
	import { IDataListItem } from '../../types';
	import { SelectItem } from '../AmeliproSelect/types';
	import { usePagination } from '../../composables/usePagination';

	const props = defineProps({
		counterLabel: {
			type: String,
			default: 'résultat(s)',
		},
		hiddenLabels: {
			type: Boolean,
			default: false,
		},
		id: {
			type: String,
			required: true,
		},
		items: {
			type: Array as PropType<IDataListItem[]>,
			default: () => [],
		},
		itemsToDisplayDesktop: {
			type: Number,
			default: 10,
		},
		itemsToDisplayMobile: {
			type: Number,
			default: 10,
		},
		noResultListInfos: {
			type: Boolean,
			default: false,
		},
		paginationSelect: {
			type: Boolean,
			default: true,
		},
		sortSelect: {
			type: Boolean,
			default: true,
		},
		sortSelectItems: {
			type: Array as PropType<SelectItem[]>,
			default: () => [],
		},
		title: {
			type: String,
			required: true,
		},
	});

	const ameliproPaginationSelect = ref<AmeliproPaginationSelect>();
	const ameliproSortSelect = ref<AmeliproSortSelect>();
	const breakpointHelper = new BreakpointHelper();

	const {
		currentPage,
		currentPageItems,
		itemToDisplay,
		paginationSelectItems,
		paginationTable,
		setDefaultItemsPerPage,
		updatePagination,
	} = usePagination(props.items, props.itemsToDisplayDesktop, props.itemsToDisplayMobile);

	watch(() => props.items, () => {
		updatePagination(props.items, itemToDisplay.value);
	});

	onMounted(() => {
		hideSelectLabel();
		setDefaultItemsPerPage();
	});

	onUpdated(() => {
		hideSelectLabel();
	});

	const hideSelectLabel = (): void => {
		if (props.hiddenLabels) {
			document.getElementById(`${props.id}-sort-select-label`)?.setAttribute('class', 'd-sr-only');
			document.getElementById(`${props.id}-pagination-select-label`)?.setAttribute('class', 'd-sr-only');
		}
	};

	const currentItems = computed<IDataListItem[]>(() => currentPageItems(props.items));

	const emit = defineEmits(['click', 'change:sort-select', 'change:pagination-select']);

	const emitClickEvent = (newCurrentPage: number): void => {
		emit('click');
		if (newCurrentPage !== null && newCurrentPage !== undefined) {
			currentPage.value = newCurrentPage;
		}
	};

	const emitSortSelectChange = (): void => {
		emit('change:sort-select', ameliproSortSelect.value?.inputValue);
	};

	const emitPaginationSelectChange = (): void => {
		emit('change:pagination-select');
		if (ameliproPaginationSelect.value && (ameliproPaginationSelect.value.inputValue !== null || ameliproPaginationSelect.value.inputValue !== undefined)) {
			updatePagination(props.items, ameliproPaginationSelect.value.inputValue);
		}

		if ((currentPage.value !== null && currentPage.value !== undefined) && currentPage.value > paginationTable.value.length) {
			currentPage.value = paginationTable.value.length;
		}

		if (currentPage.value === 0) {
			currentPage.value = 1;
		}
	};
</script>

<style lang="scss" scoped>
.total-counter {
	background-color: $ap-grey-lighten2;
	border: 1px solid $ap-grey-darken1;
	border-radius: $btn-radius;
	padding: 0.75rem 1rem;
}
</style>
